import { useQueryClient } from "@tanstack/react-query";
import { MailWarning, RefreshCw } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import {
  mutationErrorMessage,
  useEmailEvent,
  useRetryEmail,
  type EmailDeliveryView,
  type EmailEventView,
  type EmailState,
} from "../../../api/caseMutations.js";
import { caseQueryKeys } from "../../../api/caseQueries.js";
import { wholesaleQueryKeys } from "../../../api/wholesaleQueries.js";
import { isPartsRole } from "../../../../shared/domain.js";
import { useSession } from "../../../app/AppProviders.js";
import { Button } from "../../../ui/Button.js";
import { RecipientWarnings, roleLabel } from "./RecipientWarning.js";

/**
 * Email delivery failure, made visible and retryable (spec §9, §17).
 *
 * Watches one logical email event. While it is pending or sending, or once
 * every recipient has it, the banner is silent — the failure state is the
 * only one worth interrupting Parts for. When a delivery has failed it names
 * every recipient with their status and the last error, says plainly that
 * the order change stands (an arrival email failing never rolls the arrival
 * back; a correction email failing never undoes the correction), and offers
 * one Retry under the **same** event ID: the server re-queues the failed
 * deliveries with their frozen recipients, body and Message-IDs.
 *
 * Retry renders only when the server granted `canRetryEmail` *and* the
 * session is Parts. The server enforces this anyway; hiding the control is
 * presentation defence in depth.
 *
 * A wholesale order's manual customer email (`wholesale_ready`, wholesale
 * spec §5.3) shows through the same banner: the event names the order in
 * `wholesaleOrderId`, the one recipient is the customer, and the copy says
 * the order itself is unchanged — an email is never part of an arrival
 * there. The inspector lists every send with the state the order detail
 * reported (`knownState`); whenever this banner's own poll of the event
 * disagrees with it — a failure the worker recorded after the detail was
 * read, or a retry that put the event back in flight — the owner's detail
 * is re-read once for that state, so the list and the banner never
 * contradict each other.
 */

export interface EmailFailureBannerProps {
  emailEventId: string;
  /** `CaseDetail.permissions.canRetryEmail` from the server. */
  canRetry: boolean;
  /**
   * The event's state as the owner's detail last reported it (a wholesale
   * order's `emails[].state`); the detail is re-read when the polled event
   * says otherwise. Absent where the detail lists no per-email state.
   */
  knownState?: EmailState;
  className?: string;
}

const STATE_LABELS: Readonly<Record<EmailState, string>> = {
  pending: "Waiting to send",
  sending: "Sending",
  sent: "Sent",
  failed: "Failed",
};

export function hasFailedDelivery(event: EmailEventView): boolean {
  return (
    event.state === "failed" ||
    event.deliveries.some((delivery) => delivery.state === "failed")
  );
}

function kindLabel(kind: EmailEventView["kind"]): string {
  if (kind === "wholesale_ready") return "Customer email";
  return kind === "arrival_corrected"
    ? "Arrival corrected email"
    : "Arrival email";
}

const timeFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Chicago",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function formatInstant(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : timeFormatter.format(date);
}

export function EmailFailureBanner({
  emailEventId,
  canRetry,
  knownState,
  className,
}: EmailFailureBannerProps) {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const event = useEmailEvent(emailEventId);
  const retry = useRetryEmail(emailEventId);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [retryRequested, setRetryRequested] = useState(false);
  const titleId = useId();
  const recipientsId = useId();

  // `canRetry` is the server's `permissions.canRetryEmail`, computed when the
  // case detail was read. The delivery worker usually fails *after* that read
  // (the case was opened as soon as it arrived), so this banner's own poll is
  // what learns the event failed: re-read the case then, so the permission —
  // and with it the Retry control — catches up with the failure it shows.
  const failed = event.data !== undefined && hasFailedDelivery(event.data);
  const failedCaseId = failed ? event.data.caseId : null;
  const failedOrderId = failed ? event.data.wholesaleOrderId : null;
  const mayRetry = user !== null && isPartsRole(user.role);
  useEffect(() => {
    if (canRetry || !mayRetry) return;
    if (failedCaseId) {
      void queryClient.invalidateQueries({ queryKey: caseQueryKeys.detail(failedCaseId) });
    } else if (failedOrderId) {
      void queryClient.invalidateQueries({ queryKey: wholesaleQueryKeys.detail(failedOrderId) });
    }
  }, [failedCaseId, failedOrderId, canRetry, mayRetry, queryClient]);

  // The detail's own word on this event (`knownState`) is stale as soon as
  // the polled event says something else — whatever the permission already
  // is. Re-read the owner once per polled state, so a detail that keeps
  // disagreeing can never start a loop.
  const polledState = event.data?.state ?? null;
  const polledOrderId = event.data?.wholesaleOrderId ?? null;
  const polledCaseId = event.data?.caseId ?? null;
  const reReadFor = useRef<EmailState | null>(null);
  useEffect(() => {
    if (knownState === undefined || polledState === null || polledState === knownState) return;
    if (reReadFor.current === polledState) return;
    reReadFor.current = polledState;
    if (polledOrderId) {
      void queryClient.invalidateQueries({ queryKey: wholesaleQueryKeys.detail(polledOrderId) });
    } else if (polledCaseId) {
      void queryClient.invalidateQueries({ queryKey: caseQueryKeys.detail(polledCaseId) });
    }
  }, [knownState, polledState, polledOrderId, polledCaseId, queryClient]);

  // A lookup problem must never read as a delivery failure: stay quiet until
  // the server has actually described the event.
  if (!event.data) return null;
  const data = event.data;

  const failedCount = data.deliveries.filter(
    (delivery) => delivery.state === "failed",
  ).length;
  const total = data.deliveries.length;
  const showRetry = canRetry && mayRetry;

  if (!hasFailedDelivery(data)) {
    // Retry was requested and the event is back in flight: say so once, quietly.
    if (
      retryRequested &&
      (data.state === "pending" || data.state === "sending")
    ) {
      return (
        <p
          className={cx("email-banner", "email-banner--quiet", className)}
          data-tone="info"
        >
          Retry requested for the {kindLabel(data.kind).toLowerCase()}. Delivery
          runs in the background; this updates when it finishes.
        </p>
      );
    }
    return null;
  }

  const requestRetry = () => {
    setRetryError(null);
    retry.mutate(undefined, {
      onSuccess: () => setRetryRequested(true),
      onError: (error) =>
        setRetryError(`Retry didn't start: ${mutationErrorMessage(error)}`),
    });
  };

  return (
    <section
      role="alert"
      aria-labelledby={titleId}
      className={cx("email-banner", className)}
      data-tone="danger"
    >
      <div className="email-banner__header">
        <MailWarning
          className="email-banner__icon"
          aria-hidden="true"
          focusable="false"
        />
        <h3 id={titleId} className="email-banner__title">
          {kindLabel(data.kind)} didn't send
        </h3>
      </div>
      <p className="email-banner__copy">
        Delivery failed for {failedCount} of {total}{" "}
        {total === 1 ? "recipient" : "recipients"}
        {data.attempts > 0
          ? ` after ${data.attempts} ${data.attempts === 1 ? "attempt" : "attempts"}`
          : ""}
        .{" "}
        {data.kind === "wholesale_ready" ? (
          <>
            <strong>The order is unchanged</strong> — the customer simply has
            not been told yet.
          </>
        ) : data.kind === "arrival_corrected" ? (
          <>
            The <strong>correction itself is saved</strong> — the order left
            Arrived when it was made, and an email problem never undoes that.
          </>
        ) : (
          <>
            The order is <strong>still Arrived</strong> — an email problem never
            undoes a verified arrival.
          </>
        )}{" "}
        Retry re-sends only the failed deliveries, with the same recipients and
        message.
      </p>
      {data.lastError ? (
        <p className="email-banner__error">
          <span className="email-banner__label">Last error</span>{" "}
          <code className="email-banner__code">{data.lastError}</code>
        </p>
      ) : null}

      <h4 id={recipientsId} className="email-banner__subtitle">
        Recipients
      </h4>
      <ul className="email-banner__recipients" aria-labelledby={recipientsId}>
        {data.deliveries.map((delivery) => (
          <DeliveryRow key={delivery.id} delivery={delivery} />
        ))}
      </ul>

      <RecipientWarnings
        warnings={data.recipientWarnings}
        footnote="These people were left out when the email was created; retrying does not add them."
      />

      {retryError ? (
        <p className="form-alert" role="status">
          {retryError}
        </p>
      ) : null}

      {showRetry ? (
        <div className="email-banner__actions">
          <Button
            variant="primary"
            icon={<RefreshCw />}
            busy={retry.isPending}
            onClick={requestRetry}
          >
            Retry email
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function DeliveryRow({ delivery }: { delivery: EmailDeliveryView }) {
  return (
    <li className="email-banner__recipient" data-state={delivery.state}>
      <span className="email-banner__recipient-name">
        {delivery.recipientName}
      </span>{" "}
      <span className="email-banner__recipient-role">
        ({delivery.recipientRole === null ? "Customer" : roleLabel(delivery.recipientRole)})
      </span>{" "}
      <span className="email-banner__recipient-email">
        {delivery.recipientEmail}
      </span>{" "}
      <span className="email-banner__recipient-state">
        — {STATE_LABELS[delivery.state]}
        {delivery.state === "sent" && delivery.sentAt
          ? ` ${formatInstant(delivery.sentAt)}`
          : ""}
        {delivery.state === "failed" && delivery.lastError
          ? `: ${delivery.lastError}`
          : ""}
      </span>
    </li>
  );
}

function cx(...classes: (string | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}
